import * as amplitude from '@amplitude/analytics-browser';

import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PaymentIntentResult,
  StripeElements,
  StripeError,
  StripeExpressCheckoutElement,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeLinkAuthenticationElement,
  StripePaymentElement,
} from '@stripe/stripe-js';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  finalize,
  from,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { AxonService } from 'shared/src/services/axon.service';
import { LoadingService } from 'shared/src/services/loading.service';
import { LocalStorageKey, LocalStorageService } from 'shared/src/services/local-storage.service';
import { NewsbreakService } from 'shared/src/services/newsbreak.service';
import { PaymentDataService } from 'shared/src/services/payment-data.service';
import { PaymentHandlerService } from 'shared/src/services/payment-handler.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { CurrencyCode } from 'shared/src/services/api.service';
import { ProductView } from '../checkout/checkout.component';

// getStripe() swallows bootstrap/loadStripe failures (emits nothing, never errors), which
// would leave the page on its spinner forever — a timeout is the only failure signal.
const STRIPE_LOAD_TIMEOUT_MS = 15000;

export type PaymentMethodKind = 'card' | 'express';

/**
 * One-time charge configuration (e.g. credits top-up). When present, the page charges through
 * createPaymentIntent instead of creating a subscription, and every subscription-specific
 * behavior (pixels, retention header, disclaimer, email collection, subscription tracking,
 * payment-result navigation) is skipped — the host owns copy, tracking and success routing.
 */
export interface OneTimePaymentData {
  amount: number;
  currency: CurrencyCode;
  // Stripe account resolved by the host's own endpoint (e.g. the credits catalog); the
  // customer may already exist on a different account than the bootstrap default.
  publishableKey?: string;
  priceLabel: string;
  compareAtPriceLabel?: string;
  // Shown instead of the subscription retention header; omit for no header.
  title?: string;
  createPaymentIntent: () => Observable<{ clientSecret: string }>;
  // Absolute URL Stripe sends redirect-based payment methods back to.
  returnUrl: string;
  onSuccess: (method: PaymentMethodKind) => void;
  onError?: (error: unknown, method: PaymentMethodKind) => void;
}

export interface PaymentData {
  buttonTitle: string;
  email?: string;
  name?: string;
  stripeCustomerId?: string;
  isDisclaimerShown: boolean;
  insufficientFundsAction?: () => void;
  // Opt-in back button (top-left, same look as the funnel quiz steps). Only hosts that can
  // send the user somewhere meaningful set it — funnels keep their app-level back button, so
  // leaving this unset avoids a second one.
  backAction?: () => void;
  // Subscription checkout (the default; every funnel caller sets this).
  product?: ProductView;
  // One-time payment mode — see OneTimePaymentData.
  payment?: OneTimePaymentData;
  // In-app path the result page offers as a way back; subscription flows only, and only
  // for hosts that have somewhere to send the user (the web app's discount paywall).
  successRedirect?: string;
}

@Component({
  selector: 'app-payment',
  imports: [MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: PaymentData;

  @ViewChild('stripeElement') stripeElement!: ElementRef;
  @ViewChild('stripeForm') stripeForm!: ElementRef;
  @ViewChild('stripeEmail') stripeEmail!: ElementRef;
  @ViewChild('expressCheckout') expressCheckoutContainer!: ElementRef;

  isLoading: boolean = true;
  isExpressMethodsAvailable: boolean = true;
  isSubmittingPayment: boolean = false;
  isPaymentElementReady: boolean = false;
  hasLoadFailed = false;

  private stripeElements: StripeElements | undefined;
  // The express buttons run on their own Elements group: the payment element is created with
  // wallets disabled, so the two cannot share one instance.
  private stripeExpressElements: StripeElements | undefined;
  // Retained so a retry can cancel a still-pending load. Unlike the card group this chain has
  // no timeout, so it can outlive the failure that put the retry button on screen.
  private expressElementsSubscription: Subscription | undefined;
  // One-time mode: the intent created for this page, reused across retry attempts. Creating
  // a fresh intent per attempt could double-charge when a confirmation response was lost —
  // re-confirming the SAME intent is safe (Stripe rejects re-confirmation of a succeeded
  // intent instead of charging again, and allows retrying a failed one with a new method).
  private pendingIntent: { clientSecret: string; stripeCustomerId?: string } | undefined;
  private isDestroyed = false;

  // True when data.email came from Stripe collection (Link element, wallet sheet) rather than
  // the host — only such emails may be replaced by a later collection.
  private isEmailCollected = false;

  // Store element references for cleanup
  private paymentElement: StripePaymentElement | undefined;
  private expressCheckoutElement: StripeExpressCheckoutElement | undefined;
  private linkAuthElement: StripeLinkAuthenticationElement | undefined;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private loadingService: LoadingService,
    private paymentHandler: PaymentHandlerService,
    private paymentDataService: PaymentDataService,
    private axonService: AxonService,
    private newsbreakService: NewsbreakService,
    private localStorageService: LocalStorageService,
    private userInfo: UserInfoService
  ) {
    this.data = this.paymentDataService.getPaymentData()!;
  }

  get isOneTime(): boolean {
    return !!this.data.payment;
  }

  get isBackShown(): boolean {
    return !!this.data.backAction;
  }

  backClicked(): void {
    // A payment is already in flight — leaving now would strand the confirmation.
    if (this.isSubmittingPayment) return;

    this.data.backAction?.();
  }

  get amount(): number {
    return this.data.payment ? this.data.payment.amount : this.getInitialCharge(this.data.product!);
  }

  get currency(): CurrencyCode {
    return this.data.payment ? this.data.payment.currency : this.data.product!.currency;
  }

  get priceLabel(): string {
    return this.data.payment ? this.data.payment.priceLabel : this.data.product!.priceLabel;
  }

  get compareAtPriceLabel(): string | undefined {
    return this.data.payment
      ? this.data.payment.compareAtPriceLabel
      : this.data.product!.compareAtPriceLabel;
  }

  ngOnInit(): void {
    if (this.isOneTime) return;

    // Track begin_checkout for Axon pixel (subscription funnels only)
    this.axonService.beginCheckout({
      item: this.data.product!.code,
      value: this.amount,
      currency: this.currency,
    });
    //this.newsbreakService.beginCheckout();
  }

  ngAfterViewInit(): void {
    // One-time hosts (credits paywall) track their own opened event before navigating here.
    if (!this.isOneTime) {
      amplitude.track('web_all_payment_options_opened');
    }

    this.createPaymentElements();
    this.createExpressElements();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.unmountElements();
    this.paymentDataService.clearPaymentData();
  }

  onPurchaseClicked(event: Event) {
    event.preventDefault();

    if (
      this.isLoading ||
      this.isSubmittingPayment ||
      !this.isPaymentElementReady ||
      this.isDestroyed
    ) {
      return;
    }

    this.submitPayment();
  }

  retryLoad(): void {
    if (this.isDestroyed || this.isLoading) return;

    this.unmountElements();
    this.isPaymentElementReady = false;
    this.hasLoadFailed = false;
    this.isLoading = true;

    // The express group caught the same load failure and went inert — revive it too.
    this.isExpressMethodsAvailable = true;

    this.createPaymentElements();
    this.createExpressElements();
  }

  private createPaymentElements() {
    this.paymentHandler
      .createPaymentElements(
        this.amount,
        this.currency,
        this.isOneTime ? 'payment' : 'subscription',
        this.data.payment?.publishableKey
      )
      .pipe(
        take(1),
        timeout(STRIPE_LOAD_TIMEOUT_MS),
        tap(elements => {
          if (this.isDestroyed) return;

          this.stripeElements = elements;
          this.setupPaymentElement();
          this.setupEmailAuthentication();

          // Deferred: with a cached Stripe instance this tap runs synchronously inside
          // ngAfterViewInit, and flipping isLoading mid-change-detection triggers NG0100.
          setTimeout(() => {
            if (this.isDestroyed) return;
            this.isLoading = false;
          });
        }),
        catchError(error => {
          console.error('Failed to create payment elements:', error);

          if (!this.isDestroyed) {
            this.isLoading = false;
            this.hasLoadFailed = true;
          }

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private createExpressElements() {
    // Cancel any still-pending load first: with a slow loadStripe the original chain is alive
    // when retryLoad() fires, and letting both complete would mount a second element into the
    // same container and leave stripeExpressElements pointing at a different Elements group
    // than the mounted one (mismatched confirms).
    this.expressElementsSubscription?.unsubscribe();

    this.expressElementsSubscription = this.paymentHandler
      .createPaymentElements(
        this.amount,
        this.currency,
        this.isOneTime ? 'payment' : 'subscription',
        this.data.payment?.publishableKey
      )
      .pipe(
        take(1),
        tap(elements => {
          if (this.isDestroyed) return;

          this.stripeExpressElements = elements;
          this.setupExpressCheckout();
        }),
        catchError(error => {
          console.error('Failed to create express checkout elements:', error);

          // The card form owns the load-failure UI (hasLoadFailed); wallets just go away.
          this.setExpressMethodsAvailable(false);

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getInitialCharge(product: ProductView): number {
    if (product.firstPayment) {
      return product.firstPayment;
    }

    if (product.setupFeePrice) {
      return product.setupFeePrice;
    }

    if (product.introductoryRecurringPrice) {
      return product.introductoryRecurringPrice;
    }

    return product.recurringPrice;
  }

  private setupPaymentElement() {
    if (!this.stripeElements || this.isDestroyed) return;

    try {
      this.paymentElement = this.stripeElements.create('payment', {
        paymentMethodOrder: ['paypal', 'card'],
        wallets: {
          applePay: 'never',
          googlePay: 'never',
        },
        layout: {
          type: 'accordion',
          defaultCollapsed: false,
        },
      });

      this.paymentElement.on('ready', () => {
        if (this.isDestroyed) return;
        this.isPaymentElementReady = true;
      });

      this.paymentElement.on('loaderror', error => {
        if (this.isDestroyed) return;
        // Without this the pay button would stay disabled forever with no explanation
        // (e.g. Stripe blocked by the network or a content blocker) — show a retry instead.
        this.isPaymentElementReady = false;
        this.hasLoadFailed = true;
        console.error('Payment element load error:', error);
      });

      this.paymentElement.mount(this.stripeElement.nativeElement);
    } catch (error) {
      this.isPaymentElementReady = false;
      this.hasLoadFailed = true;
      console.error('Failed to create payment element:', error);
    }
  }

  private setupExpressCheckout() {
    if (!this.stripeExpressElements || this.isDestroyed) return;

    try {
      this.expressCheckoutElement = this.stripeExpressElements.create('expressCheckout', {
        buttonHeight: 48,
        paymentMethods: {
          googlePay: 'auto',
          applePay: 'auto',
          paypal: 'auto',
          link: 'auto',
        },
        layout: {
          maxColumns: 3,
          maxRows: 3,
        },
      });

      // With a click listener attached, Stripe waits for resolve() before opening the sheet.
      // Unknown buyer: make the sheet collect the email (handed back in the confirm event's
      // billingDetails), or the subscription would be created for an anonymous customer.
      this.expressCheckoutElement.on('click', event => {
        event.resolve(!this.data.email && !this.isOneTime ? { emailRequired: true } : undefined);
      });

      // Set up event listeners before mounting
      this.expressCheckoutElement.on('ready', (event: StripeExpressCheckoutElementReadyEvent) => {
        if (this.isDestroyed) return;

        // Element is ready, check if any methods are actually available
        const hasAvailableMethods = !!event.availablePaymentMethods;

        if (!hasAvailableMethods) {
          console.log('Express checkout has no available payment methods');
        }

        this.setExpressMethodsAvailable(hasAvailableMethods);
      });

      this.expressCheckoutElement.on('loaderror', event => {
        if (this.isDestroyed) return;

        console.error('Express checkout error:', event);
        this.setExpressMethodsAvailable(false);
      });

      this.expressCheckoutElement.on(
        'confirm',
        (event: StripeExpressCheckoutElementConfirmEvent) => {
          if (this.isDestroyed) return;

          this.submitExpressPayment(event);
        }
      );

      // Now mount the element
      this.expressCheckoutElement.mount(this.expressCheckoutContainer.nativeElement);
    } catch (error) {
      console.error('Failed to create express checkout:', error);
      this.setExpressMethodsAvailable(false);
    }
  }

  // Deferred to a macrotask: with a cached Stripe instance parts of the chain resolve
  // synchronously inside ngAfterViewInit, and flipping this mid-change-detection triggers
  // NG0100.
  private setExpressMethodsAvailable(isAvailable: boolean): void {
    setTimeout(() => {
      if (this.isDestroyed) return;
      this.isExpressMethodsAvailable = isAvailable;
    });
  }

  private setupEmailAuthentication() {
    // One-time flows arrive with a known user (e.g. userId-guarded /credits), so no
    // email collection there.
    if (!this.stripeElements || this.data.email || this.isOneTime || this.isDestroyed) return;

    try {
      this.linkAuthElement = this.stripeElements.create('linkAuthentication');

      // The email typed here otherwise reaches only Stripe (billing_details): the app would
      // create the subscription without it, the purchase event would carry no email, and the
      // backend could not map this visitor's amplitudeDeviceId to a user. Feed it back before
      // submit so createCharge and the result page both know who is buying.
      this.linkAuthElement.on('change', event => {
        if (this.isDestroyed || !event.complete || !event.value.email) return;

        this.adoptCollectedEmail(event.value.email);
      });

      this.linkAuthElement.mount(this.stripeEmail.nativeElement);
    } catch (error) {
      console.error('Failed to create link authentication:', error);
    }
  }

  /**
   * Adopts an email Stripe collected for us (Link element, wallet sheet). An email the host
   * resolved upfront (paywall identification, quiz) always wins — it is the one the
   * subscription must be filed under. A previously *collected* value, however, is replaced by
   * the latest one: "a@b.co" can be a keystroke on the way to "a@b.com", and a retried payment
   * may go through a wallet account with a different address.
   */
  private adoptCollectedEmail(email: string): void {
    if (this.data.email && !this.isEmailCollected) return;

    this.isEmailCollected = true;
    this.data.email = email;
    // The result page and the upsell flow read identity from UserInfoService/localStorage.
    this.userInfo.setEmail(email);
  }

  private submitPayment() {
    if (
      !this.stripeElements ||
      this.isDestroyed ||
      this.isSubmittingPayment ||
      !this.isPaymentElementReady
    )
      return;

    // Capture elements reference to avoid issues if component is destroyed mid-flow
    const elements = this.stripeElements;
    this.isSubmittingPayment = true;

    from(elements.submit())
      .pipe(
        tap(({ error: submitError }) => {
          if (submitError) {
            throw submitError;
          }
        }),
        switchMap(() => {
          if (this.isDestroyed) return EMPTY;

          this.loadingService.start();
          return this.createCharge();
        }),
        switchMap(charge => this.confirmCharge(elements, charge, 'card')),
        tap(result => this.handlePaymentSuccess(result, 'card')),
        catchError(error => this.handlePaymentError(error, 'card')),
        finalize(() => {
          this.isSubmittingPayment = false;
        })
      )
      .subscribe();
  }

  private submitExpressPayment(event: StripeExpressCheckoutElementConfirmEvent) {
    // Rejected confirms must fail the wallet sheet or its payment interface stays
    // pending over the page.
    if (!this.stripeExpressElements || this.isDestroyed || this.isSubmittingPayment) {
      event.paymentFailed({ reason: 'fail' });
      return;
    }

    const elements = this.stripeExpressElements;
    this.isSubmittingPayment = true;

    // Must happen before createCharge(): the wallet's email is what the backend files the
    // Stripe customer (and the amplitudeDeviceId → user mapping) under.
    if (event.billingDetails?.email) {
      this.adoptCollectedEmail(event.billingDetails.email);
    }

    this.loadingService.start();

    this.createCharge()
      .pipe(
        switchMap(charge => this.confirmCharge(elements, charge, 'express', event)),
        tap(result => this.handlePaymentSuccess(result, 'express', event)),
        catchError(error => this.handlePaymentError(error, 'express', event)),
        finalize(() => {
          this.isSubmittingPayment = false;
        })
      )
      .subscribe();
  }

  private createCharge(): Observable<{ clientSecret: string; stripeCustomerId?: string }> {
    if (this.data.payment) {
      if (this.pendingIntent) {
        return of(this.pendingIntent);
      }

      return this.data.payment
        .createPaymentIntent()
        .pipe(tap(intent => (this.pendingIntent = intent)));
    }

    return this.paymentHandler
      .createSubscription({
        productId: this.data.product!.id,
        email: this.data.email,
        customerId: this.data.stripeCustomerId,
        name: this.data.name,
        amplitudeDeviceId: amplitude.getDeviceId(),
      })
      .pipe(
        tap(({ stripeCustomerId }) => this.userInfo.setStripeCustomerId(stripeCustomerId)),
        tap(() => this.savePaymentValue())
      );
  }

  private confirmCharge(
    elements: StripeElements,
    { clientSecret, stripeCustomerId }: { clientSecret: string; stripeCustomerId?: string },
    method: PaymentMethodKind,
    expressEvent?: StripeExpressCheckoutElementConfirmEvent
  ): Observable<PaymentIntentResult> {
    // Check if component is still alive before confirming payment
    if (this.isDestroyed) {
      expressEvent?.paymentFailed({ reason: 'fail' });
      return EMPTY;
    }

    const returnUrl = this.data.payment
      ? this.data.payment.returnUrl
      : this.paymentHandler.getPaymentResultReturnUrl(
          this.data.product!.code,
          stripeCustomerId!,
          this.data.successRedirect
        );

    return this.paymentHandler
      .confirmPayment(
        elements,
        clientSecret,
        returnUrl,
        this.data.payment?.publishableKey,
        this.isOneTime ? 'payment' : 'subscription'
      )
      .pipe(
        catchError(error => {
          // PaymentHandlerService.confirmPayment already showed the payment-error modal
          // before rethrowing — re-handling here would stack a second dialog.
          this.loadingService.finish();
          expressEvent?.paymentFailed({ reason: 'fail' });

          if (!this.paymentHandler.isNonFailureError(error)) {
            this.data.payment?.onError?.(error, method);
          }

          return EMPTY;
        })
      );
  }

  private handlePaymentSuccess(
    result: PaymentIntentResult,
    method: PaymentMethodKind,
    expressEvent?: StripeExpressCheckoutElementConfirmEvent
  ) {
    this.loadingService.finish();

    // The user may have navigated away while the confirmation was in flight — don't yank
    // them back to the success page (or fire host callbacks) from wherever they are now.
    if (this.isDestroyed) return;

    if (result.error) {
      // Modal already shown by PaymentHandlerService.confirmPayment.
      expressEvent?.paymentFailed({ reason: 'fail' });

      if (!this.paymentHandler.isNonFailureError(result.error)) {
        this.data.payment?.onError?.(result.error, method);
      }

      return;
    }

    if (this.data.payment) {
      this.data.payment.onSuccess(method);
      return;
    }

    this.paymentHandler.navigateToSuccess(
      this.data.product!.code,
      this.userInfo.getStripeCustomerId() || '',
      this.data.successRedirect
    );
  }

  private handlePaymentError(
    error: StripeError | Error | any,
    method: PaymentMethodKind,
    expressEvent?: StripeExpressCheckoutElementConfirmEvent
  ) {
    // Only charge-creation and elements.submit failures reach here — confirmation errors
    // are handled (and their modal shown) inside confirmCharge.
    // Always clear the loading overlay, even when suppressing the dialog/navigation,
    // so it doesn't stay stuck on the page the user navigated to.
    this.loadingService.finish();

    // Even when the component is already destroyed, the wallet sheet must be told the
    // attempt failed or it stays pending.
    expressEvent?.paymentFailed({ reason: 'fail' });

    // Don't surface a dialog or navigate if the user already left checkout.
    if (error?.message === 'Component destroyed' || this.isDestroyed) {
      return of(null);
    }

    // Form-validation and element-lifecycle errors are not payment failures — reporting
    // them to the host would pollute its failure metrics (an incomplete card number is
    // not a failed purchase).
    if (!this.paymentHandler.isNonFailureError(error)) {
      this.data.payment?.onError?.(error, method);
    }

    this.paymentHandler.handlePaymentError(
      error,
      this.data.insufficientFundsAction,
      this.data.email,
      this.data.product?.code,
      this.isOneTime ? 'payment' : 'subscription',
      this.data.successRedirect
    );

    return of(null);
  }

  private savePaymentValue() {
    this.localStorageService.set(LocalStorageKey.paymentValue, {
      value: this.amount,
      currency: this.currency,
    });
  }

  private unmountElements() {
    // Unmount all Stripe elements to prevent "Element is no longer mounted" errors
    try {
      this.paymentElement?.unmount();
    } catch {
      // Element may already be unmounted
    }

    try {
      this.expressCheckoutElement?.unmount();
    } catch {
      // Element may already be unmounted
    }

    try {
      this.linkAuthElement?.unmount();
    } catch {
      // Element may already be unmounted
    }

    this.paymentElement = undefined;
    this.expressCheckoutElement = undefined;
    this.linkAuthElement = undefined;
    this.stripeElements = undefined;
    this.stripeExpressElements = undefined;
  }
}
